import React, { memo, useCallback, useState } from 'react';
import {
  AlignWidget,
  CheckboxWidget,
  TextWidget,
} from '@plone/volto/components/manage/Widgets';
import FileWidget from '@plone/volto/components/manage/Widgets/FileWidget';
import Icon from '@plone/volto/components/theme/Icon/Icon';
import { flattenToAppURL } from '@plone/volto/helpers/Url/Url';
import PropTypes from 'prop-types';
import { defineMessages, FormattedMessage, injectIntl } from 'react-intl';
import { Accordion, Form, Segment } from 'semantic-ui-react';

import clearSVG from '@plone/volto/icons/clear.svg';
import downSVG from '@plone/volto/icons/down-key.svg';
import navTreeSVG from '@plone/volto/icons/nav.svg';
import upSVG from '@plone/volto/icons/up-key.svg';

const messages = defineMessages({
  Image: {
    id: 'Image',
    defaultMessage: 'Image',
  },
  AltText: {
    id: 'Alt text',
    defaultMessage: 'Descrição da imagem (texto ALT).',
  },
  ImageCaption: {
    id: 'Image caption',
    defaultMessage: 'Image caption',
  },
  Align: {
    id: 'Alignment',
    defaultMessage: 'Alignment',
  },
  LinkTo: {
    id: 'Link to',
    defaultMessage: 'Link to',
  },
  openLinkInNewTab: {
    id: 'Open in a new tab',
    defaultMessage: 'Open in a new tab',
  },
  LinkSettings: {
    id: 'Link settings',
    defaultMessage: 'Configurações do link',
  },
});

const LeadImageSidebar = ({
  properties,
  data,
  block,
  onChangeBlock,
  openObjectBrowser,
  required = false,
  onChangeField,
  intl,
}) => {
  const [activeAccIndex, setActiveAccIndex] = useState(0);

  const handleAccClick = useCallback((e, { index }) => {
    setActiveAccIndex((prevIndex) => (prevIndex === index ? -1 : index));
  }, []);

  return (
    <Segment.Group raised>
      <header className="header pulled">
        <h2>
          <FormattedMessage id="Lead Image" defaultMessage="Lead Image" />
        </h2>
      </header>

      <Segment className="form sidebar-image-data">
        <Form.Field inline>
          <FileWidget
            id="leadimage_upload"
            title={intl.formatMessage(messages.Image)}
            value={properties.image || null}
            onChange={(id, value) => {
              onChangeField('image', value);
            }}
          />
        </Form.Field>
        <TextWidget
          id="alt"
          title={intl.formatMessage(messages.AltText)}
          required={!!properties.image}
          value={properties.alt}
          icon={properties.alt ? clearSVG : null}
          iconAction={() => onChangeField('alt', '')}
          onChange={(name, value) => {
            onChangeField('alt', value);
          }}
        />
        <TextWidget
          id="image_caption"
          title={intl.formatMessage(messages.ImageCaption)}
          value={properties.image_caption}
          icon={properties.image_caption ? clearSVG : null}
          iconAction={() => onChangeField('image_caption', '')}
          onChange={(name, value) => {
            onChangeField('image_caption', value);
          }}
        />
        <Form.Field inline required={required}>
          <AlignWidget
            value={data.align}
            actions={['left', 'narrow', 'right', 'center', 'full']}
            onChange={(name, value) => {
              onChangeBlock(block, {
                ...data,
                align: value,
              });
            }}
            title={intl.formatMessage({
              id: 'Alignment',
              defaultMessage: 'Alignment',
            })}
          />
        </Form.Field>
      </Segment>
      <Accordion fluid styled className="form">
        <Accordion.Title
          active={activeAccIndex === 0}
          index={0}
          onClick={handleAccClick}
        >
          <FormattedMessage {...messages.LinkSettings} />
          {activeAccIndex === 0 ? (
            <Icon name={upSVG} size="20px" />
          ) : (
            <Icon name={downSVG} size="20px" />
          )}
        </Accordion.Title>
        <Accordion.Content active={activeAccIndex === 0}>
          <TextWidget
            id="link"
            title={intl.formatMessage(messages.LinkTo)}
            required={false}
            value={data.href ? flattenToAppURL(data.href) : ''}
            icon={data.href ? clearSVG : navTreeSVG}
            iconAction={
              data.href
                ? () => {
                    onChangeBlock(block, {
                      ...data,
                      href: '',
                    });
                  }
                : () => openObjectBrowser({ mode: 'link' })
            }
            onChange={(name, value) => {
              onChangeBlock(block, {
                ...data,
                href: flattenToAppURL(value),
              });
            }}
          />
          <CheckboxWidget
            id="openLinkInNewTab"
            title={intl.formatMessage(messages.openLinkInNewTab)}
            value={!!data.openLinkInNewTab}
            onChange={(name, value) => {
              onChangeBlock(block, {
                ...data,
                openLinkInNewTab: value,
              });
            }}
          />
        </Accordion.Content>
      </Accordion>
    </Segment.Group>
  );
};

LeadImageSidebar.propTypes = {
  data: PropTypes.objectOf(PropTypes.any).isRequired,
  block: PropTypes.string.isRequired,
  onChangeBlock: PropTypes.func.isRequired,
  openObjectBrowser: PropTypes.func.isRequired,
  onChangeField: PropTypes.func.isRequired,
  properties: PropTypes.shape({
    image: PropTypes.any,
    alt: PropTypes.string,
    image_caption: PropTypes.string,
  }).isRequired,
  required: PropTypes.bool,
  intl: PropTypes.shape({
    formatMessage: PropTypes.func.isRequired,
  }).isRequired,
};

export default injectIntl(memo(LeadImageSidebar));
